import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import getTiptapNodeTextContent from '../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import { MNHotInstance } from '../tableHotInstanceTypes';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';
import { FormulaDropdownRef } from '../FormulaDropdown';

// Utils
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';
import { createFormulaDropdownSession, type FormulaDropdownSession } from './formulaAutocompleteSessionUtils';

export const FORMULA_AUTOCOMPLETE_PLUGIN_NAME = 'milanoteFormulaAutocomplete';

type PluginSettings = {
    dropdownRef: React.RefObject<FormulaDropdownRef>;
};

class FormulaAutocompleteHotPlugin extends MilanoteHotPlugin<PluginSettings> {
    private formulaDropdownSession: FormulaDropdownSession | null;

    static get PLUGIN_KEY(): string {
        return FORMULA_AUTOCOMPLETE_PLUGIN_NAME;
    }

    constructor(hotInstance: MNHotInstance) {
        super(hotInstance);

        this.formulaDropdownSession = null;
    }

    /**
     * Enable the plugin.
     */
    enablePlugin(): void {
        this.addHook('afterBeginEditing', () => this.onAfterBeginEditing());
        this.addHook('afterSelection', () => this.onAfterSelection());

        this.addMilanoteHook('onMilanoteCellEditorChange', (content: TiptapContent) =>
            this.onMilanoteCellEditorChange(content),
        );
        this.addMilanoteHook('onMilanoteCellEditorSelectionChange', () => this.onMilanoteCellEditorSelectionChange());

        super.enablePlugin();
    }

    /**
     * Update the plugin.
     */
    updatePlugin(): void {
        this.disablePlugin();
        this.enablePlugin();

        super.updatePlugin();
    }

    /******************
     * PLUGIN HOOKS
     ******************/

    onAfterBeginEditing(): void {
        this.formulaDropdownSession = null;
    }

    onAfterSelection(): void {
        this.removeDropdown();
    }

    onMilanoteCellEditorChange(content: TiptapContent): void {
        const cellText = getTiptapNodeTextContent(content);

        if (!isFormula(cellText)) {
            return this.removeDropdown();
        }

        if (!this.formulaDropdownSession && cellText === '=') {
            return this.openFormulaDropdown(cellText);
        }

        const selection = this.milanoteHot.getMNEditor()?.getPlainTextSelection();
        if (!selection) {
            return this.removeDropdown();
        }

        this.updateDropdownForCellText(cellText, selection.from);
    }

    onMilanoteCellEditorSelectionChange(): void {
        const selection = this.milanoteHot.getMNEditor()?.getPlainTextSelection();
        if (!this.formulaDropdownSession || !selection) return;

        const { queryStartOffset, queryEndOffset } = this.formulaDropdownSession;
        const isCursorWithinQueryRange = selection.from >= queryStartOffset && selection.from <= queryEndOffset;
        if (isCursorWithinQueryRange) return;

        this.removeDropdown();
    }

    /******************
     * HELPER FUNCTIONS
     ******************/

    openFormulaDropdown(cellContent: string): void {
        const selection = this.milanoteHot.getMNEditor()?.getPlainTextSelection();
        this.formulaDropdownSession = createFormulaDropdownSession(cellContent, selection?.from ?? cellContent.length);
        this.updateDropdownForCellText(cellContent, selection?.from ?? cellContent.length);
    }

    updateDropdownForCellText(newCellContent: string, cursorOffset: number): void {
        const { dropdownRef } = this.getConfig();
        if (!dropdownRef?.current) return;

        if (!this.formulaDropdownSession || cursorOffset < this.formulaDropdownSession.queryStartOffset) {
            return this.removeDropdown();
        }

        const query = newCellContent.slice(this.formulaDropdownSession.queryStartOffset, cursorOffset);

        this.formulaDropdownSession = {
            ...this.formulaDropdownSession,
            queryEndOffset: cursorOffset,
        };

        dropdownRef.current.setFormulaDropdownQuery(query);
    }

    removeDropdown(): void {
        const { dropdownRef } = this.getConfig();
        if (!dropdownRef?.current) return;

        this.formulaDropdownSession = null;

        dropdownRef.current.hideFormulaDropdown();
    }

    /**
     * Destroy the plugin.
     */
    destroy(): void {
        this.removeDropdown();
        super.destroy();
    }
}

export default FormulaAutocompleteHotPlugin;
